// ── 행 카드 (데스크톱 compact + 모바일 카드 레이아웃) ─────────────────────────
import React from 'react';
import { formatItemsDisplay } from '../../../utils/contractFormat';
import { useIsDark } from '../../../hooks/useDarkMode';
import { resolveRowState } from './rowState';
import {
  rowCardStyle, rowCardDesktopStyle,
  desktopRowInner, desktopCompanyStyle, desktopDivider, desktopInfoChip,
  rowTopStyle, rowCompanyStyle, infoGridStyle, infoLabel, infoValue,
  resultRowStyle, contractedBadge, cancelledBadge,
  contractDetailStyle, serviceBadge, feedbackBadge,
  reorderBtnStyle, editBtnStyle, deleteContractBtnStyle, historyBtnStyle,
} from './pageStyles';

// 그리드 셀 — 레이블 + 값 (모바일 전용)
function InfoItem({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={infoLabel}>{label}</span>
      <span style={infoValue}>{value || '-'}</span>
    </div>
  );
}

export function ContractRow({ row, isMobile, onEdit, onReorder, onDeleteRequest, onHistory }) {
  const isDark = useIsDark();
  // 행 상태(색/필터의 단일 진실) — utils.resolveRowState. 배경색과 필터가 어긋나지 않게 한다.
  const rowState = resolveRowState(row);
  const isCancelled = rowState === 'cancelled';
  // contract_items 배열 기반 표시 (주계약/서비스 분리)
  const contractItems = Array.isArray(row.contract_items) ? row.contract_items : [];
  const contractDisplay = formatItemsDisplay(contractItems);
  const serviceDetail   = formatItemsDisplay(contractItems, { serviceOnly: true }) !== '-'
    ? formatItemsDisplay(contractItems, { serviceOnly: true })
    : null;

  // 유효기간 — contract_items의 validityMonths 값에서 추출, 중복 제거 후 "N개월" 표시
  const validityText = (() => {
    const months = [...new Set(
      contractItems.filter((i) => !i.isService && i.validityMonths).map((i) => i.validityMonths)
    )];
    return months.length > 0 ? months.map((m) => `${m}개월`).join('/') : null;
  })();

  // 택배사·송장번호 — delivery_items 배열 우선, 없으면 레거시 단일 필드
  const firstDelivery = Array.isArray(row.delivery_items) && row.delivery_items.length > 0
    ? row.delivery_items[0]
    : null;
  const courierText  = firstDelivery?.courier       || row.courier         || null;
  const trackingText = firstDelivery?.tracking_number || row.tracking_number || null;
  const isPickup = Array.isArray(row.delivery_items) && row.delivery_items.some((i) => i.is_pickup);

  // 택배사 + 송장번호가 모두 입력된 항목(또는 직접수령)이 하나라도 있으면 배송완료(초록) — rowState 로 파생
  const hasShipped = rowState === 'shipped';

  // ── 데스크톱: 1줄 compact 레이아웃 ────────────────────────────────────────
  if (!isMobile) {
    return (
      <div style={rowCardDesktopStyle(row.is_contracted, isCancelled, hasShipped, isDark)}>
        <div style={desktopRowInner}>
          {/* 칩: 상태 배지 */}
          {row.is_contracted ? (
            isCancelled
              ? <span style={cancelledBadge}>계약취소</span>
              : <span style={contractedBadge}>계약완료</span>
          ) : (
            row.feedback && <span style={feedbackBadge}>{row.feedback}</span>
          )}
          {/* 날짜: 계약일 우선, 없으면 콜일자 표시 */}
          {(row.contract_date || row.call_date) && (
            <span style={desktopInfoChip}>{row.contract_date || row.call_date}</span>
          )}
          {/* 상호명 */}
          <span style={desktopCompanyStyle}>{row.company_name || '-'}</span>
          {/* 신규/재주문 칩 — 계약완료 행만 표시 */}
          {row.is_contracted && (
            <span style={row.is_reorder
              ? { fontSize: 11, fontWeight: 700, color: '#c2410c', background: 'rgba(234,88,12,0.10)', border: '1px solid rgba(234,88,12,0.3)', borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap', flexShrink: 0 }
              : { fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap', flexShrink: 0 }
            }>
              {row.is_reorder ? '재주문' : '신규'}
            </span>
          )}
          <span style={desktopDivider} />
          {/* 티켓종류 × 장수 */}
          {contractDisplay !== '-' && (
            <span style={{ ...desktopInfoChip, color: '#166534', fontWeight: 600 }}>{contractDisplay}</span>
          )}
          {/* 서비스 */}
          {serviceDetail && (
            <span style={{ ...desktopInfoChip, color: '#6d28d9', fontWeight: 600 }}>서비스: {serviceDetail}</span>
          )}
          {/* 유효기간 */}
          {validityText && <span style={desktopInfoChip}>{validityText}</span>}
          {/* 휴대폰 */}
          {row.mobile_phone && <span style={desktopInfoChip}>{row.mobile_phone}</span>}
          {/* 주소 — 전체 표시 */}
          {row.address && (
            <span style={desktopInfoChip}>{row.address}</span>
          )}
          {/* 상담자 */}
          {row.user_name && <span style={desktopInfoChip}>{row.user_name}</span>}
          {/* 마케터 */}
          {row.sales_person_name && <span style={desktopInfoChip}>· {row.sales_person_name}</span>}
          {/* 택배사 + 송장번호 — 둘 다 있을 때만 표시, 없고 직접수령이면 '직접수령' */}
          {courierText && trackingText ? (
            <>
              <span style={desktopInfoChip}>{courierText}</span>
              <span style={desktopInfoChip}>{trackingText}</span>
            </>
          ) : isPickup && <span style={desktopInfoChip}>직접수령</span>}
          {/* 비고 — 앰버 틴트로 구별, 말줄임 처리, 호버 시 전체 내용 툴팁 */}
          {/* flex 컨테이너 내 말줄임: minWidth:0 필수 — 기본값 auto면 내용 길이만큼 늘어나 overflow가 작동 안 함 */}
          {row.memo && (
            <span
              title={row.memo}
              style={{
                ...desktopInfoChip,
                minWidth: 0, maxWidth: 140, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)',
                borderRadius: 6, padding: '1px 8px',
              }}
            >
              {row.memo}
            </span>
          )}
        </div>
        {/* 버튼 그룹 */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
          {row.is_contracted && !isCancelled && onReorder && (
            <button style={reorderBtnStyle} onClick={() => onReorder(row)}>재주문</button>
          )}
          {/* 완료 배지 — 수정 버튼 바로 왼쪽 */}
          {row.is_contracted && !isCancelled && hasShipped && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: 'rgba(22,163,74,0.12)', borderRadius: 6, padding: '2px 10px', border: '1px solid rgba(22,163,74,0.3)', whiteSpace: 'nowrap' }}>완료</span>
          )}
          <button style={editBtnStyle(row.is_contracted)} onClick={() => onEdit(row)}>
            {row.is_contracted ? '수정' : '계약 등록'}
          </button>
          {row.is_contracted && onDeleteRequest && (
            <button style={deleteContractBtnStyle} onClick={() => onDeleteRequest(row)}>삭제</button>
          )}
          {row.is_contracted && onHistory && (
            <button style={historyBtnStyle} onClick={() => onHistory(row)}>이력</button>
          )}
        </div>
      </div>
    );
  }

  // ── 모바일: 기존 카드 레이아웃 ─────────────────────────────────────────────
  return (
    <div style={rowCardStyle(row.is_contracted, isCancelled, hasShipped, isDark)}>
      {/* 상호 + 담당자 */}
      <div style={rowTopStyle}>
        {/* 배지 + 접수일자 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 2 }}>
          {row.is_contracted ? (
            <>
              {isCancelled
                ? <span style={cancelledBadge}>계약취소</span>
                : <span style={contractedBadge}>계약완료</span>
              }
            </>
          ) : (
            row.feedback && <span style={feedbackBadge}>{row.feedback}</span>
          )}
          {/* 날짜: 계약일 우선, 없으면 콜일자 표시 */}
          {(row.contract_date || row.call_date) && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{row.contract_date || row.call_date}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <strong style={rowCompanyStyle}>{row.company_name || '-'}</strong>
          {/* 신규/재주문 칩 */}
          {row.is_contracted && (
            <span style={row.is_reorder
              ? { fontSize: 11, fontWeight: 700, color: '#c2410c', background: 'rgba(234,88,12,0.10)', border: '1px solid rgba(234,88,12,0.3)', borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }
              : { fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }
            }>
              {row.is_reorder ? '재주문' : '신규'}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
          {row.user_name          && <span>· 상담자: {row.user_name}</span>}
          {row.sales_person_name  && <span>· 마케터: {row.sales_person_name}</span>}
        </div>
      </div>

      {/* 상세 정보 그리드 */}
      <div style={infoGridStyle}>
        <InfoItem label="계약일자" value={row.contract_date || row.visit_date} />
        <InfoItem label="전화"     value={row.phone || row.mobile_phone} />
        <InfoItem label="주소"     value={row.address} />
      </div>

      {/* 계약/서비스 상세 */}
      {row.is_contracted && (
        <div style={resultRowStyle}>
          {contractDisplay !== '-' && <span style={contractDetailStyle}>{contractDisplay}</span>}
          {serviceDetail && <span style={serviceBadge}>서비스: {serviceDetail}</span>}
        </div>
      )}

      {/* 버튼 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
        {row.is_contracted && !isCancelled && onReorder && (
          <button style={reorderBtnStyle} onClick={() => onReorder(row)}>재주문 등록</button>
        )}
        {/* 완료 배지 — 수정 버튼 바로 왼쪽 */}
        {row.is_contracted && !isCancelled && hasShipped && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: 'rgba(22,163,74,0.12)', borderRadius: 6, padding: '2px 10px', border: '1px solid rgba(22,163,74,0.3)', whiteSpace: 'nowrap' }}>완료</span>
        )}
        <button style={editBtnStyle(row.is_contracted)} onClick={() => onEdit(row)}>
          {row.is_contracted ? '수정' : '계약 등록'}
        </button>
        {row.is_contracted && onDeleteRequest && (
          <button style={deleteContractBtnStyle} onClick={() => onDeleteRequest(row)}>삭제</button>
        )}
        {row.is_contracted && onHistory && (
          <button style={historyBtnStyle} onClick={() => onHistory(row)}>이력</button>
        )}
      </div>
    </div>
  );
}
